import React, { useMemo } from 'react';
// eslint-disable-next-line jira/restricted/styled-components, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { ThemeProvider } from 'styled-components';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { GenericPermissionErrorPageAsync } from '@atlassian/jira-error-pages/src/async.tsx';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import type { RequestSecuritySettingsResponse } from '@atlassian/jira-router-resources-service-desk-customer-permissions/src/services/customer-permissions-page/get-data/types.tsx';
import { customerPermissionsPageDataResource } from '@atlassian/jira-router-resources-service-desk-customer-permissions/src/services/customer-permissions-page/index.tsx';
import { ServiceDeskAnalyticsContext } from '@atlassian/jira-servicedesk-analytics/src/ui/servicedesk-analytics-context/index.tsx';
import { ProjectSubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/project-sub-product-updater/index.tsx';
import { LicenseCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/license/index.tsx';
import { ProjectSettingsController } from '@atlassian/jira-spa-apps-project-settings-common/src/components/routing-filter';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/mark-product-start/index.tsx';
import { useRouter, useResource } from '@atlassian/react-resource-router';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { ServiceDeskCustomerPermissions } from './ui';

export const ServiceDeskCustomerPermissionsPage = () => {
	const { data: projectContext } = useResource(projectContextResource);
	const customerPermissionsResource = useResource(customerPermissionsPageDataResource);

	const updateCustomerPermissionsResource = customerPermissionsResource.update;
	const customerPermissionsState = useMemo(
		() => ({
			loading: customerPermissionsResource.loading,
			data: customerPermissionsResource.data,
			error: customerPermissionsResource.error,
			update: (newData: RequestSecuritySettingsResponse) => {
				updateCustomerPermissionsResource(() => newData);
			},
		}),
		[
			customerPermissionsResource.loading,
			customerPermissionsResource.data,
			customerPermissionsResource.error,
			updateCustomerPermissionsResource,
		],
	);

	const [{ match }] = useRouter();
	const { projectKey } = match.params;
	return (
		<>
			<MarkProductStart />
			<ProjectSettingsController projectKey={projectKey}>
				<ProjectSubProductUpdater />
				<ThemeProvider theme={{}}>
					<ServiceDeskAnalyticsContext>
						<JSErrorBoundary
							id="customerPermissionsSettingsPage"
							packageName="jiraSpaAppsProjectSettingsServicedeskCustomerPermissions"
							onError={() => {
								GlobalPageLoadExperience.failure({ force: true });
							}}
							sendToPrivacyUnsafeSplunk
							teamName="jsd-flux-capacitors"
						>
							<LicenseCheck type="servicedesk">
								{({ result: isLicensed }) =>
									isLicensed ? (
										<ServiceDeskCustomerPermissions
											projectContext={projectContext}
											customerPermissionsState={customerPermissionsState}
										/>
									) : (
										<GenericPermissionErrorPageAsync />
									)
								}
							</LicenseCheck>
						</JSErrorBoundary>
					</ServiceDeskAnalyticsContext>
				</ThemeProvider>
			</ProjectSettingsController>
		</>
	);
};

export default ServiceDeskCustomerPermissionsPage;
